import * as React from 'react';
import { Title } from '../../utils/title';
import { translateDataSourceResultGroups, toDataSourceRequestString, FilterDescriptor } from '@progress/kendo-data-query';
import { Grid, GridColumn as Column, GridToolbar, GridDataStateChangeEvent, GridColumnMenuFilter, GridColumnProps, isColumnMenuFilterActive, GridColumnMenuWrapper, GridCustomHeaderCellProps, GridFilterOperators, GridColumnMenuProps, operators } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { fetchApi } from '../../services/api';
import ColumnMenu from '../LoadBoard/BidHistory/ColumnMenu';
import { quoteIDCell } from '../Quote/Recent';
import ThreadIDCell from '../Quote/Recent/ThreadIDCell';
import OrderNumberCell from '../Quote/Recent/OrderNumberCell';
import { debounce } from 'ts-debounce';
import { State as DataState } from '@progress/kendo-data-query';
import DateCell from '../../components/cells/DateCell';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { CustomFilterUIDropdown } from 'components/CustomFilterUIDropDown';
import { orderStatuses } from 'views/Track/AdvancedSearch';
import { arrowRotateCwIcon, filterClearIcon, sortAscSmallIcon, sortDescSmallIcon } from '@progress/kendo-svg-icons';
import { GridLoadingPanel } from 'components/GridLoadingPanel';
import { dateFormatter } from 'utils/utils';
import { formatNumber } from '@progress/kendo-intl';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { IconWrap } from '@progress/kendo-react-common';
import dateTimeReviver from '../../utils/dateTimeReviver';

type Props = {};

type State = {
  dataState: DataState;
  data: QuoteValue[],
  total: number;
  vehicleId: number;
  vehicles: Array<{ ID: number; Name: string }>;
  filterText: string;
  loading: boolean;
}

type QuoteValue = {
  QuoteID: number;
  ThreadID: string;
  RouteName: string;
  Distance: number;
  Total: number;
  CustomerNumber: string;
  VehicleTypeName: string;
  FromLocation: string;
  FromDateTime: Date;
  ToLocation: string;
  CreatedDateTime: Date;
  AcceptedOfferCount: number;
  DeclinedOfferCount: number;
  ExpiredOfferCount: number;
  Pieces: number;
  Weight: number;
  OrderID: null | number;
  OrderNumber: null | number;
  OrderStatus: null | number;
  VehicleCountAvailable: null | number;
  CreatedByUserName: string;
  IsTemplate: boolean;
  Unassigned: boolean;
  Margin: number | null;
  MarginPercentage: number | null;
}

const startsWithFilterOperators: GridFilterOperators = {
  ...operators,
  text: [
    ...operators.text.filter((x) => x.operator === 'startswith'),
    ...operators.text.filter((x) => x.operator !== 'startswith'),
  ],
};

const StartsWithColumnMenu = (props: GridColumnMenuProps) => <ColumnMenu {...props} filterOperators={startsWithFilterOperators} />;

export const HeaderWithIcon = (props: GridCustomHeaderCellProps) => {
  const { thProps, onClick, columnMenuWrapperProps, field, title } = props;

  // children = [default header cell container, resizer]; keep the resizer
  const [, resizer] = React.Children.toArray(props.children);
  const sort = columnMenuWrapperProps.sort?.find((d) => d.field === field);

  return (
    <HeaderThElement {...thProps} style={{ paddingInline: 8 }}>
      <span className="k-cell-inner" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span className="k-link" onClick={onClick}>
          <span className="k-column-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span>{title ?? field ?? '\u00A0'}</span>
            <img src="/images/truck_emoji.png" alt="Truck" height={18} width={18} />
          </span>
          {sort && (
            <span className="k-sort-icon">
              <IconWrap icon={sort.dir === 'asc' ? sortAscSmallIcon : sortDescSmallIcon} />
            </span>
          )}
        </span>
        {columnMenuWrapperProps.columnMenu && <GridColumnMenuWrapper {...columnMenuWrapperProps} />}
      </span>
      {resizer}
    </HeaderThElement>
  );
};

class QuoteHistory extends React.Component<Props, State> {

  private export: React.RefObject<ExcelExport>;

  constructor(props: Props) {
    super(props);

    let dataState: DataState = {
      skip: 0,
      take: 50,
      sort: [{field: "QuoteID", dir: "desc"}],
      filter: { logic: 'and', filters: [] }
    };
    const sessionStorageDataState = sessionStorage.getItem("QuoteHistory-DataState");
    if (sessionStorageDataState) {
      dataState = JSON.parse(sessionStorageDataState, dateTimeReviver);
    }

    this.state = {
      dataState,
      data: [],
      total: 0,
      vehicles: [],
      vehicleId: 0,
      filterText: '',
      loading: false,
    };

    this.export = React.createRef();

    this.reset = this.reset.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.dataStateChange = this.dataStateChange.bind(this);
    this.filterTextChange = this.filterTextChange.bind(this);
  }

  private columnProps(field: string): Partial<GridColumnProps> {
    return {
        field: field,
        columnMenu: ColumnMenu,
        headerClassName: this.isColumnActive(field, this.state.dataState) ? 'active' : ''
    };
  }

  private isColumnActive(field: string, dataState: any) {
    return isColumnMenuFilterActive(field, dataState.filter);
  }

  public componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const offeredVehicleId = urlParams.get('offeredVehicleId');
    if (offeredVehicleId != null) {
      this.setState({ vehicleId: parseInt(offeredVehicleId) }, this.fetchData);
    } else {
      this.fetchData();
    }

    fetchApi("/api/Track/AssetNumbers")
      .then((data: { Drivers: Array<{ ID: number, Name: string }>, Vehicles: Array<{ ID: number, Name: string }> }) => {
        this.setState({ vehicles: data.Vehicles });

        // Pre-populate filter text
        if (offeredVehicleId != null) {
          const vehicle = data.Vehicles.find(v => v.ID === parseInt(offeredVehicleId));
          if (vehicle != null) {
            this.setState({ filterText: vehicle.Name });
          }
        }
      });
  }

  public render() {
    const hasTemplateFilter = this.hasFilterDescriptor(this.state.dataState.filter?.filters as FilterDescriptor[], 'IsTemplate');
    return (
      <React.Fragment>
        <Title string="Quote History" />
        {this.state.loading && <GridLoadingPanel />}
        <ExcelExport ref={this.export}>
          <Grid sortable={{ allowUnsort: true }}
              pageable={{ pageSizes: [20, 50, 100, 500] }}
              scrollable={'none'}
              total={this.state.total}
              data={this.state.data}
              onDataStateChange={this.dataStateChange}
              {...this.state.dataState}
              >
            <GridToolbar>
            <Button
              title="Clear Filters and Sort"
              icon="filter-clear"
              svgIcon={filterClearIcon}
              onClick={this.reset}
            />
            <Button
              title="Refresh"
              icon="refresh"
              svgIcon={arrowRotateCwIcon}
              onClick={this.fetchData}
            />
            <Button
              onClick={() => {
                const parsedData = this.state.data.map((item) => ({
                  ...item,
                  FromDateTime: dateFormatter(item.FromDateTime),
                  CreatedDateTime: dateFormatter(item.CreatedDateTime),
                }));
                this.export.current.save(parsedData);
              }}
            >Excel
            </Button>
            <Input placeholder="Unit # Offered" value={this.state.filterText} onChange={(value) => {
              this.setState({ filterText: value.value.toUpperCase() }, () => {
                this.filterTextChangeDebounced();
              });
            }} />
            <Button
              togglable
              selected={hasTemplateFilter}
              onClick={() => this.dataStateChange({ dataState: {
                ...this.state.dataState,
                filter: { logic: 'and', filters: hasTemplateFilter ? [] : [{ field: 'IsTemplate', operator: 'eq', value: true }] }
              } } as GridDataStateChangeEvent)
            }
            >Templates
            </Button>
            </GridToolbar>
            <Column
              width="120px"
              {...this.columnProps("QuoteID")}
              title="#"
              filter="numeric"
              cells={{ data: quoteIDCell() }}
            />
            <Column {...this.columnProps("ThreadID")} columnMenu={StartsWithColumnMenu} title="Front" cells={{ data: ThreadIDCell }} />
            <Column {...this.columnProps("OrderNumber")} title="Order No." filter="numeric" cells={{ data: OrderNumberCell }} />
            <Column {...this.columnProps("OrderStatus")} title="Status" filter="numeric"
              cells={{ data: (props) => {
                const status = props.dataItem.OrderStatus;
                return <td {...props.tdProps}>{status != null ? orderStatuses.find(x => x.ID == status)?.Name : ''}</td>;
              }}}
              columnMenu={(props) =>
                <GridColumnMenuFilter
                    {...props}
                    expanded
                    filterUI={(props) =>
                        <CustomFilterUIDropdown
                          {...props}
                          data={orderStatuses}
                          textField="Name"
                          dataItemKey="ID"
                          operator='eq'
                          defaultItem={{ ID: 0, Name: "Status" }}
                        />
                    }
                />
              }
            />
            <Column {...this.columnProps("VehicleCountAvailable")} title="INS" filter="numeric" cells={{ headerCell: HeaderWithIcon }} />
            <Column {...this.columnProps("RouteName")} columnMenu={StartsWithColumnMenu} title="Route" className="wrap-anywhere" />
            <Column {...this.columnProps("Distance")} title="Miles" filter="numeric" format="{0:n0}" />
            <Column {...this.columnProps("Pieces")} filter="numeric" format="{0:n0}" />
            <Column {...this.columnProps("Weight")} filter="numeric" format="{0:n0}" />
            <Column {...this.columnProps("Total")} title="Charges" filter="numeric" format="{0:c}" />
            <Column field="Margin" filterable={false} format="{0:c}"
              cells={{ data: (props) => {
                return <td {...props.tdProps}>{props.dataItem.Margin ? `${formatNumber(props.dataItem.Margin, 'c')}${props.dataItem.MarginPercentage ? ` (${formatNumber(props.dataItem.MarginPercentage, 'p0')})` : ''}` : '\u2013'}</td>;
              }}}
            />
            <Column {...this.columnProps("VehicleTypeName")} title="Vehicle Type" />
            <Column {...this.columnProps("CustomerNumber")} columnMenu={StartsWithColumnMenu} title="Bill To" />
            <Column {...this.columnProps("FromLocation")} title="From" />
            <Column {...this.columnProps("ToLocation")} title="To" />
            <Column {...this.columnProps("FromDateTime")} title="Ship Date" filter="date" cells={{ data: DateCell }} />
            <Column {...this.columnProps("CreatedDateTime")} title="Created" filter="date" cells={{ data: DateCell }} />
            <Column {...this.columnProps("CreatedByUserName")} columnMenu={StartsWithColumnMenu} title="By" />
          </Grid>
        </ExcelExport>
      </React.Fragment>
    );
  }

  private filterTextChangeDebounced = debounce(this.filterTextChange, 250);

  private filterTextChange() {
    // Vehicle Number Search
    const vehicleResult = this.state.vehicles.find(x => x.Name === this.state.filterText);
    if (vehicleResult && vehicleResult.ID != this.state.vehicleId) {
      this.setState({ vehicleId: vehicleResult.ID }, this.fetchData);
    } else if (this.state.vehicleId > 0 && !this.state.filterText) {
      this.setState({ vehicleId: 0 }, this.fetchData);
    }
  }

  private reset() {
    this.dataStateChange({ dataState: {
      skip: 0,
      take: 50,
      sort: [{field: "QuoteID", dir: "desc"}],
      filter: { logic: 'and', filters: [] }
    } } as GridDataStateChangeEvent);
  }

  private dataStateChange(changeEvent: GridDataStateChangeEvent) {
    sessionStorage.setItem('QuoteHistory-DataState', JSON.stringify(changeEvent.dataState));
    this.setState({ dataState: changeEvent.dataState }, () => this.fetchData());
  }

  private hasFilterDescriptor = (filters: FilterDescriptor[] | undefined, field: string) => {
    if (!filters) return false;
    return filters.filter(x => x.field == field).length > 0;
  }

  private fetchData() {
    this.setState({ loading: true });

    const dataState = this.state.dataState;

    const queryStr = `${toDataSourceRequestString(dataState)}`; // Serialize the state
    const hasGroups = dataState.group && dataState.group.length;

    fetchApi(`/api/Quote/History_Read?${queryStr}`, { VehicleID: this.state.vehicleId }, 'POST')
        .then(({ Data, Total }) => {
            this.setState({
                data: hasGroups ? translateDataSourceResultGroups(Data) : Data,
                total: Total,
                dataState,
                loading: false,
            });
        }).catch(() => {
          this.setState({ loading: false });
        });
  }
}

export default QuoteHistory;
